/* =====================================================================
   BeezFy — Video in evidenza sulle card prodotto
   ---------------------------------------------------------------------
   Obiettivo: una collezione lunga piena di prodotti con video deve pesare
   quanto pesa oggi. Per questo il <video> NON sta nell'HTML: lo creo solo
   quando la card entra in vista e lo distruggo quando è lontana.

   Perché non basta scrivere <video preload="none" poster="...">:
   il poster viene scaricato SEMPRE e SUBITO (non esiste loading="lazy"
   sui poster). Su 60 card sarebbero 60 immagini in più al primo colpo.
   Qui il "poster" è già l'<img loading="lazy"> della card, che sta sotto.

   ---------------------------------------------------------------------
   DUE PRINCIPI, da tenere distinti (settembre 2026)

   1) LA RIGA È L'UNITÀ, NON LA CARD.
      Quattro card affiancate con due che si muovono e due congelate si
      leggono come un caricamento rotto, non come una scelta. Quindi il
      numero di video insieme NON è un numero fisso: è il numero di colonne
      della griglia, misurato dal vivo. O la riga si muove tutta, o niente.

   2) LA QUALITÀ È LA MANOPOLA, NON IL NUMERO.
      Se serve risparmiare si abbassa la definizione, non si spengono card.

      ⚠️ QUI L'HLS NON VA USATO, ed è controintuitivo. Prima le card
      preferivano l'm3u8 "perché è a banda adattiva". Misurato sul live con
      Chrome (che l'HLS lo suona nativamente, canPlayType = "maybe"): due
      sole card scaricavano 10,7 MB su desktop e 10,8 MB su TELEFONO, dove
      la card è larga 189 px. Il motivo è che l'HLS si adatta alla BANDA,
      non alla dimensione di rendering: con una connessione veloce sale al
      gradino più alto e infila un 1080p in un francobollo. Ottimizza la
      variabile sbagliata.

      Quindi qui si sceglie noi l'mp4 giusto. Shopify pubblica ogni video in
      tre profili sul CDN (480p 1,5 Mbps / 720p 4,5 Mbps / 1080p 7,2 Mbps);
      Liquid espone solo il più pesante, ma l'URL degli altri è derivabile
      per sostituzione — stesso hash, stesso id finale, cambia solo il
      segmento centrale. La scelta si può fare solo qui, perché Liquid non
      conosce né la densità dello schermo né la banda.

      Il livello si sceglie in due passaggi: il più leggero che copre i
      pixel veri della card, poi si scende finché la RIGA INTERA sta sotto
      un tetto di banda. Così poche card grandi prendono qualità alta e
      molte card piccole restano leggere, senza mai spegnere nessuno.

      Nella scheda prodotto, dove il video è grande, l'HLS resta corretto:
      questo file riguarda solo le card.

   Doc: docs/VIDEO-IN-EVIDENZA.md
   ===================================================================== */
(function () {
  'use strict';

  var SEL = '[data-card-video]';
  var PLAY_RATIO = 0.5;    // quanto deve essere visibile per partire
  var MARGIN = '200px';    // quanto prima creare il player
  var HOVER_DELAY = 90;    // ms: senza, attraversare la griglia col mouse
                           // istanzierebbe un player per ogni card sfiorata
  var MAX_ROW = 6;         // rete di sicurezza se una griglia ha molte colonne

  // ---- Profili che Shopify genera sul CDN, dal più leggero al più pesante.
  // La larghezza è quella reale del rendition (il nome dice l'altezza).
  var PROFILES = [
    { key: 'SD-480p-1.5Mbps',  w: 384, mbps: 1.5 },
    { key: 'HD-720p-4.5Mbps',  w: 576, mbps: 4.5 },
    { key: 'HD-1080p-7.2Mbps', w: 864, mbps: 7.2 }
  ];
  // Tetto di banda per l'INTERA riga in riproduzione. Con 4 colonne fa
  // 4×1,5 = 6 Mbps (passa) contro 4×4,5 = 18 (non passa) → 480p. Con 2 sole
  // colonne, dove le card sono grandi il doppio, 2×4,5 = 9 passa → 720p.
  // È il tetto a decidere, quindi il conto resta sensato da solo se domani
  // il merchant cambia il numero di colonne.
  var ROW_BUDGET_MBPS = 10;
  var RE_PROFILE = /\.(SD-480p-1\.5Mbps|HD-720p-4\.5Mbps|HD-1080p-7\.2Mbps)-/;

  function attr(name) { return document.documentElement.getAttribute(name); }

  // ---- Movimento: è accessibilità, non si scavalca mai. Nemmeno col mouse.
  function motionOk() {
    try {
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    } catch (e) { /* in dubbio, si procede */ }
    return true;
  }

  // ---- Rete: 'off' = niente autoplay (resta la foto), 'low' = solo 480p.
  // Si rilegge ogni volta: passare da wifi a cellulare deve contare subito.
  function netTier() {
    try {
      var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (!c) return 'full';
      if (c.saveData) return 'off';
      var et = c.effectiveType || '';
      if (/(^|-)2g$/.test(et)) return 'off';
      if (et === '3g') return 'low';
      if (typeof c.downlink === 'number' && c.downlink > 0 && c.downlink < 5) return 'low';
    } catch (e) { /* in dubbio, si procede */ }
    return 'full';
  }

  // ---- Quanti pixel veri deve coprire il video in questa card.
  // Il cap a 1.5 invece di devicePixelRatio pieno è voluto: un video in
  // movimento tollera molto più ridimensionamento di una foto ferma (il
  // codec stesso scarta dettaglio nei fotogrammi in movimento), quindi
  // spingere a 2x paga banda per una nitidezza che l'occhio non recupera.
  function neededWidth(el) {
    var w = el.getBoundingClientRect().width || 0;
    if (!w) {
      var card = el.closest ? el.closest('[data-product-card]') : null;
      if (card) w = card.getBoundingClientRect().width || 0;
    }
    if (!w) w = 360;   // non misurabile: valore prudente
    var dpr = window.devicePixelRatio || 1;
    return Math.round(w * Math.min(dpr, 1.5));
  }

  // ---- Riscrive l'URL sul profilo giusto. Se il nome del file non è nella
  // forma attesa (video vecchi, CDN che cambia schema) si lascia com'è: mai
  // costruire un URL che potrebbe non esistere senza avere un ripiego.
  function retarget(url, needW, cap) {
    if (!url || !RE_PROFILE.test(url)) return url;

    var forced = attr('data-card-video-quality');
    var pick = null;
    if (forced === '480') pick = PROFILES[0];
    else if (forced === '720') pick = PROFILES[1];

    if (!pick) {
      // 1) il più leggero che copre i pixel veri della card
      var idx = PROFILES.length - 1;
      for (var i = 0; i < PROFILES.length; i++) {
        if (PROFILES[i].w >= needW) { idx = i; break; }
      }
      // 2) e poi si scende finché la riga intera sta sotto il tetto
      var n = cap && cap > 0 ? cap : 1;
      while (idx > 0 && PROFILES[idx].mbps * n > ROW_BUDGET_MBPS) idx--;
      pick = PROFILES[idx];
    }
    return url.replace(RE_PROFILE, '.' + pick.key + '-');
  }

  // ---- Quante colonne ha la griglia che contiene questa card.
  // Si misura da `grid-template-columns` calcolato, che il browser risolve
  // in una lista di tracce reali: è esatto e non dipende dal sapere a quali
  // breakpoint la griglia cambia. Se il merchant cambia le colonne
  // dall'editor, questo resta giusto da solo.
  function gridColumns(el) {
    var n = el, depth = 0;
    while (n && n !== document.body && depth++ < 6) {
      try {
        var cs = getComputedStyle(n);
        if (cs.display === 'grid' || cs.display === 'inline-grid') {
          var t = (cs.gridTemplateColumns || '').trim();
          if (t && t !== 'none') {
            var cols = t.split(/\s+/).length;
            if (cols >= 1) return Math.min(cols, MAX_ROW);
          }
        }
      } catch (e) { /* niente */ }
      n = n.parentNode;
    }
    return 0;
  }

  // Il tema può forzare un numero fisso; `data-card-video-max` è il vecchio
  // nome, tenuto per i fork non ancora allineati.
  function forcedCap() {
    var n = parseInt(attr('data-card-video-cap') || attr('data-card-video-max'), 10);
    return (n && n >= 1) ? n : 0;
  }

  var holders = [];        // { el, video, ratio, offSlide, dy, dx, rowKey }
  var byEl = new WeakMap();
  var currentCap = 0;      // quante card sta tenendo accese la riga corrente:
                           // serve a create() per dimensionare la qualità
  var hovered = null;
  var hoverTimer = null;
  var canHover = false;
  try {
    canHover = !!(window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches)
               && attr('data-card-video-hover') !== 'off';
  } catch (e) { /* niente */ }

  // La foto attualmente visibile nella card: in gallery è la slide attiva,
  // altrimenti l'immagine principale.
  function currentCardImage(layer) {
    var card = layer.closest ? layer.closest('[data-product-card]') : null;
    if (!card) return '';
    var img = card.querySelector('.bz-card__gallery-slide--active img')
           || card.querySelector('.bz-card__media-image--primary')
           || card.querySelector('.bz-card__media-image');
    if (!img) return '';
    return img.currentSrc || img.src || '';
  }

  function create(h) {
    if (h.video) return h.video;
    var v = document.createElement('video');

    // L'mp4 scelto da noi viene PRIMA dell'HLS: vedi il punto 2 in testa al
    // file: sulle card l'HLS si adatta alla banda e sale a 1080p dentro un
    // riquadro da 189 px. L'm3u8 resta solo come ultima spiaggia, per i
    // video che non espongono un mp4.
    var hls = h.el.getAttribute('data-video-hls');
    var mp4 = h.el.getAttribute('data-video-src');
    var src = mp4;
    var usingMp4 = !!mp4;
    if (!src && hls && v.canPlayType && v.canPlayType('application/vnd.apple.mpegurl')) {
      src = hls;
      usingMp4 = false;
    }
    if (!src) return null;

    if (usingMp4) {
      // Rete scarsa: si scende al profilo più leggero invece di spegnere
      // card, così la riga resta omogenea.
      var needW = netTier() === 'low' ? 0 : neededWidth(h.el);
      var retargeted = retarget(src, needW, currentCap);
      if (retargeted !== src) {
        h.fallbackSrc = src;   // se il profilo derivato non esistesse
        src = retargeted;
      }
    }

    v.muted = true;            // obbligatorio per l'autoplay sui browser mobili
    v.defaultMuted = true;
    v.loop = true;
    v.playsInline = true;
    v.setAttribute('muted', '');
    v.setAttribute('playsinline', '');
    v.setAttribute('webkit-playsinline', '');
    v.setAttribute('disablepictureinpicture', '');
    v.setAttribute('disableremoteplayback', '');
    v.setAttribute('tabindex', '-1');
    v.preload = 'auto';        // creato solo quando serve: qui il preload va bene

    // Poster = la foto che la card sta GIA' mostrando. Senza, fra la comparsa
    // dell'elemento e il primo fotogramma decodificato si vede un lampo del
    // riquadro vuoto del player. L'immagine è già nella cache del browser:
    // non è una richiesta in più.
    var shown = currentCardImage(h.el);
    if (shown) v.poster = shown;

    // Se l'URL derivato non risponde si torna UNA volta a quello originale
    // dato da Liquid, che esiste per certo.
    v.addEventListener('error', function () {
      if (!h.fallbackSrc || h.triedFallback) return;
      h.triedFallback = true;
      try {
        v.src = h.fallbackSrc;
        v.load();
        var p = v.play();
        if (p && p.catch) p.catch(function () { /* resta la foto */ });
      } catch (e) { /* niente */ }
    });

    v.src = src;

    // Ripiego per i browser senza requestVideoFrameCallback: 'playing' scatta
    // all'avvio della riproduzione, un filo prima del primo fotogramma.
    if (typeof v.requestVideoFrameCallback !== 'function') {
      v.addEventListener('playing', function () { h.el.classList.add('is-playing'); });
    }

    h.el.appendChild(v);
    h.video = v;
    return v;
  }

  function destroy(h) {
    if (!h.video) return;
    h.el.classList.remove('is-playing');
    try {
      h.video.pause();
      h.video.removeAttribute('src');
      h.video.load();          // libera il buffer invece di tenerlo in memoria
    } catch (e) { /* niente */ }
    if (h.video.parentNode) h.video.parentNode.removeChild(h.video);
    h.video = null;
    h.triedFallback = false;
  }

  function pause(h) {
    if (!h.video) return;
    h.el.classList.remove('is-playing');
    try { h.video.pause(); } catch (e) { /* niente */ }
  }

  function play(h) {
    var v = create(h);
    if (!v) return;
    var p = v.play();
    if (p && p.catch) p.catch(function () { /* autoplay negato: resta la foto */ });

    // La rivelazione va rifatta a OGNI ripresa, non solo alla creazione:
    // tornando sulla prima slide il video ripartiva ma restava invisibile.
    if (v.readyState >= 2) {
      h.el.classList.add('is-playing');
    } else if (typeof v.requestVideoFrameCallback === 'function') {
      v.requestVideoFrameCallback(function () { h.el.classList.add('is-playing'); });
    }
  }

  // Chi passa il mouse su una card ha CHIESTO quel video: è intenzione
  // dichiarata, non ambiente. Quindi si aggiunge al budget invece di rubare
  // lo slot a un altro — col mouse si punta una card sola, quindi il massimo
  // reale è "riga + 1" — e vale anche quando l'autoplay ambientale è spento
  // per rete lenta. L'unica cosa che non scavalca è prefers-reduced-motion.
  function arbitrate() {
    if (!motionOk() || document.hidden) {
      holders.forEach(pause);
      return;
    }

    var winners = [];
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var cy = vh / 2;
    var cx = window.innerWidth / 2;

    // Visibilità VERA, misurata sul viewport. Non si usa `h.ratio`
    // dell'IntersectionObserver perché è calcolato contro la radice allargata
    // da rootMargin (200px): misurato, una card con lo 0,25 di sé in schermo
    // veniva dichiarata allo 0,94, e una del tutto fuori allo 0,07. Va
    // benissimo per decidere quando creare e distruggere il player — che è
    // il suo mestiere qui — ma non per decidere chi suona.
    holders.forEach(function (h) {
      var r = h.el.getBoundingClientRect();
      h.rect = r;
      h.visible = r.height > 0
        ? (Math.min(r.bottom, vh) - Math.max(r.top, 0)) / r.height
        : 0;
    });

    if (netTier() !== 'off') {
      var candidates = holders.filter(function (h) {
        return h.visible >= PLAY_RATIO && !h.offSlide;
      });

      candidates.forEach(function (h) {
        var r = h.rect;
        // dy a scatti di 4px: le card della STESSA riga devono risultare
        // esattamente pari, altrimenti il rumore subpixel decide al posto
        // nostro. A parità di riga vince chi è più vicino al centro
        // orizzontale: prima era l'ordine del DOM, quindi partivano sempre
        // e solo le due a sinistra.
        h.dy = Math.round(Math.abs(r.top + r.height / 2 - cy) / 4);
        h.dx = Math.abs(r.left + r.width / 2 - cx);
        h.rowKey = Math.round(r.top / 8);   // stessa riga = stesso bordo alto
      });
      candidates.sort(function (a, b) { return (a.dy - b.dy) || (a.dx - b.dx); });

      var cap = forcedCap();
      if (!cap) cap = candidates.length ? (gridColumns(candidates[0].el) || 2) : 2;
      currentCap = cap;

      // Si vince per RIGHE INTERE, non per singole card. I prodotti con video
      // sono sparsi nella griglia, non contigui: prendere semplicemente le
      // prime `cap` per distanza accendeva due card di una riga e due di
      // quella sotto, lasciando mezze righe spente — cioè esattamente
      // l'effetto che si voleva togliere. Una riga o entra tutta, o non entra.
      var byRow = {}, rowKeys = [];
      candidates.forEach(function (h) {
        if (!byRow[h.rowKey]) { byRow[h.rowKey] = []; rowKeys.push(h.rowKey); }
        byRow[h.rowKey].push(h);
      });
      rowKeys.sort(function (a, b) { return byRow[a][0].dy - byRow[b][0].dy; });

      for (var ri = 0; ri < rowKeys.length; ri++) {
        var group = byRow[rowKeys[ri]];
        if (!winners.length && group.length > cap) { winners = group.slice(0, cap); break; }
        if (winners.length + group.length > cap) break;
        winners = winners.concat(group);
        if (winners.length >= cap) break;
      }
    }

    // Basta che se ne veda un pezzo: se il mouse ci arriva sopra, quella card
    // è per definizione in schermo.
    if (hovered && !hovered.offSlide && hovered.visible > 0.05 && winners.indexOf(hovered) === -1) {
      if (!currentCap) currentCap = gridColumns(hovered.el) || 2;
      winners.push(hovered);
    }

    holders.forEach(function (h) {
      if (winners.indexOf(h) === -1) pause(h);
    });
    winners.forEach(play);
  }

  // ---- Hover: DELEGA sul documento, non un listener per card.
  //
  // Prima ogni card riceveva i propri mouseenter/mouseleave al momento in cui
  // veniva osservata, e tutte condividevano UN SOLO timer. Due fragilita':
  //   - una card che arriva dopo (Carica altri, filtri, quickview) o che viene
  //     sostituita nel DOM resta senza listener, e l'hover su quella non fa
  //     nulla per sempre;
  //   - passando veloci da una card all'altra, l'uscita dalla prima poteva
  //     annullare il timer appena avviato dalla seconda.
  // Con la delega non esiste un "momento dell'aggancio": qualunque card, anche
  // comparsa un secondo fa, funziona. Il timer e' uno solo perche' il puntatore
  // e' uno solo, ma viene riarmato sul cambio di card, non azzerato dall'uscita.
  var hoverCard = null;

  function holderForCard(card) {
    for (var i = 0; i < holders.length; i++) {
      var el = holders[i].el;
      if (card.contains(el)) return holders[i];
    }
    return null;
  }

  function onPointerMove(e) {
    if (!canHover) return;
    var t = e.target;
    var card = (t && t.closest) ? t.closest('[data-product-card]') : null;
    if (card === hoverCard) return;          // stessa card: niente da fare
    hoverCard = card;
    clearTimeout(hoverTimer);

    if (!card) {                              // uscito da ogni card
      if (hovered) { hovered = null; arbitrate(); }
      return;
    }
    var h = holderForCard(card);
    if (!h) {                                 // card senza video
      if (hovered) { hovered = null; arbitrate(); }
      return;
    }
    hoverTimer = setTimeout(function () {
      hovered = h;
      arbitrate();
    }, HOVER_DELAY);
  }

  if (canHover) {
    document.addEventListener('mouseover', onPointerMove, true);
    // il puntatore che esce dalla finestra deve spegnere, altrimenti resta acceso
    document.addEventListener('mouseleave', function () {
      clearTimeout(hoverTimer); hoverCard = null;
      if (hovered) { hovered = null; arbitrate(); }
    });
  }

  // Lo strato video sta SOPRA le slide della card. Se l'utente sfoglia la
  // gallery, senza questo il video resterebbe davanti e sembrerebbe che la
  // gallery non scorra: qui si ritira (e si mette in pausa) appena si esce
  // dalla prima slide, e torna quando ci si rientra.
  function bindGallery(h) {
    var card = h.el.closest ? h.el.closest('[data-product-card]') : null;
    if (!card) return;
    var gal = card.querySelector('[data-card-gallery]');
    if (!gal) return;

    var onChange = function (index) {
      h.offSlide = index !== 0;
      if (h.offSlide) { pause(h); h.el.classList.remove('is-playing'); }
      arbitrate();
    };

    // l'evento del componente gallery non fa bubbling: si ascolta sul contenitore
    gal.addEventListener('gallery:change', function (e) {
      onChange((e && e.detail && e.detail.index) || 0);
    });

    // rete di sicurezza per i percorsi che non emettono l'evento (swipe, ecc.)
    if (window.MutationObserver) {
      var track = gal.querySelector('[data-gallery-track]') || gal;
      new MutationObserver(function () {
        var slides = gal.querySelectorAll('[data-gallery-slide]');
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].classList.contains('bz-card__gallery-slide--active')) { onChange(i); return; }
        }
      }).observe(track, { attributes: true, attributeFilter: ['class'], subtree: true });
    }
  }

  function observe(root) {
    var nodes = (root || document).querySelectorAll(SEL);
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window)) return;   // niente IO: resta la foto

    Array.prototype.forEach.call(nodes, function (el) {
      if (byEl.has(el)) return;
      var h = { el: el, video: null, ratio: 0, dy: 0, dx: 0, offSlide: false };
      byEl.set(el, h);
      holders.push(h);
      io.observe(el);
      bindGallery(h);
    });
  }

  var io = ('IntersectionObserver' in window)
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var h = byEl.get(entry.target);
          if (!h) return;
          h.ratio = entry.isIntersecting ? entry.intersectionRatio : 0;
          // fuori anche dal margine di cortesia: butto via il player
          if (!entry.isIntersecting) {
            if (hovered === h) hovered = null;
            destroy(h);
          }
        });
        arbitrate();
      }, { rootMargin: MARGIN, threshold: [0, PLAY_RATIO, 0.9] })
    : null;

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) holders.forEach(pause);
    else arbitrate();
  });

  // Passare da wifi a cellulare (o attivare il risparmio dati) deve contare
  // subito, non al prossimo scroll.
  try {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.addEventListener) conn.addEventListener('change', function () { arbitrate(); });
  } catch (e) { /* niente */ }

  // Tornando indietro dalla scheda prodotto la pagina viene ripresa dalla cache
  // del browser (bfcache): l'IntersectionObserver non riscatta perché nulla si è
  // mosso, e i video distrutti allo scroll non tornavano più. Qui rimisuro tutto.
  // (Prima questa funzione ciclava su `roots()`, che non è mai esistita in
  // questo file: era un copia-incolla da beezfy-installments.js, e lanciava
  // ReferenceError a ogni focus della finestra.)
  function recheck() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    holders.forEach(function (h) {
      var r = h.el.getBoundingClientRect();
      var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
      h.ratio = r.height > 0 ? Math.max(0, visible) / r.height : 0;
    });
    arbitrate();
  }
  window.addEventListener('pageshow', function (e) { if (e.persisted) recheck(); });
  window.addEventListener('focus', recheck);

  // Ruotare il telefono o ridimensionare la finestra cambia sia le colonne
  // sia la larghezza della card: il conto va rifatto.
  var rsTimer = null;
  window.addEventListener('resize', function () {
    clearTimeout(rsTimer);
    rsTimer = setTimeout(recheck, 200);
  });

  function init() { observe(document); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Card aggiunte dopo (carica altri, filtri, quickview)
  document.addEventListener('beezfy:cards-rendered', function (e) {
    observe((e && e.detail && e.detail.container) || document);
  });
  if (window.MutationObserver) {
    var mo = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        if (muts[i].addedNodes && muts[i].addedNodes.length) { observe(document); return; }
      }
    });
    document.addEventListener('DOMContentLoaded', function () {
      mo.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
